import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MapPin, Send, Clock } from "lucide-react";
import { useI18n } from "@/i18n/translations";
import { site } from "@/data/site";
import { Seo } from "@/components/Seo";
import { DemoBadge, FotoSlot, PageHeader } from "@/components/Editorial";
import { Reveal } from "@/components/Reveal";

type FormState = { name: string; email: string; subject: string; message: string; consent: boolean; website: string };

// Invio tramite FormSubmit (servizio gratuito, nessun pannello né chiave):
// la destinazione è scritta nell'URL stesso; il primo invio genera una mail
// di attivazione sulla casella info@cspdlm.it del Centro.
const FORMSUBMIT_URL = "https://formsubmit.co/ajax/info@cspdlm.it";

const EMPTY: FormState = { name: "", email: "", subject: "", message: "", consent: false, website: "" };

export const Contatti = () => {
  const { t, lang } = useI18n();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim() || !form.consent) {
      toast.error(t("con.err_required"));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(FORMSUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          oggetto: form.subject,
          messaggio: form.message,
          privacy: form.consent ? "Sì" : "No",
          lingua: lang,
          _subject: form.subject.trim() || "Nuovo messaggio dal sito cspdlm.it",
          _template: "table",
          _captcha: "false",
          _honey: form.website,
        }),
      });
      const data = await res.json().catch(() => null);
      const ok = data?.success === true || data?.success === "true";
      if (!res.ok || !ok) {
        // form non ancora attivato: il servizio genera la mail di attivazione su info@
        if (res.status === 401 || /activ/i.test(String(data?.message ?? ""))) {
          toast.info(t("con.activate"));
          return;
        }
        throw new Error(String(res.status));
      }
      toast.success(t("con.success"));
      setForm(EMPTY);
    } catch {
      toast.error(t("con.error"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-input bg-cardwhite px-4 py-3 text-sm text-bodytx placeholder:text-mutedtx/50 transition-colors focus:border-bordeaux";
  const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/70";

  return (
    <>
      <Seo titleKey="seo.contatti.title" descKey="seo.contatti.desc" path="/contatti" />
      <PageHeader
        kickerKey="con.kicker"
        titleKey="con.title"
        asideWide
        aside={
          <FotoSlot
            src="https://thumb.wikimedia.org/wikipedia/commons/thumb/7/7a/Genova_Universit%C3%A0_di_Genova_Cortile_6.jpg/960px-Genova_Universit%C3%A0_di_Genova_Cortile_6.jpg"
            captionKey="foto.contatti"
            testid="foto-contatti"
            ratio="aspect-[21/9] sm:aspect-auto sm:min-h-[170px]"
            fill
          />
        }
      />

      <div className="container-x grid gap-12 py-16 sm:py-20 lg:grid-cols-12">
        {/* SEGRETERIA */}
        <Reveal testid="segreteria-card" className="lg:col-span-5">
          <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink">{t("con.segretaria_title")}</h2>
          <div className="mt-8 divide-y divide-borderc border border-borderc bg-cardwhite">
            <div className="flex items-start gap-4 p-6">
              <Mail className="mt-0.5 h-5 w-5 text-bordeaux" aria-hidden="true" />
              <div>
                <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/70">{t("con.email_label")}</span>
                <a href={`mailto:${site.email}`} data-testid="contact-secretariat-email" className="link-underline mt-1 inline-block text-sm font-medium text-ink">
                  {site.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6">
              <MapPin className="mt-0.5 h-5 w-5 text-bordeaux" aria-hidden="true" />
              <div>
                <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/70">{t("con.sede_label")}</span>
                <span className="mt-1 block text-sm font-medium text-ink">{t("con.sede_value")}</span>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6">
              <Clock className="mt-0.5 h-5 w-5 text-bordeaux" aria-hidden="true" />
              <div>
                <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/70">{t("con.orario_label")}</span>
                <span className="mt-1 block text-sm font-medium text-mutedtx">{t("con.orario_value")}</span>
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <DemoBadge />
            <p className="text-xs italic text-mutedtx">{t("con.privacy_provv")}</p>
          </div>
        </Reveal>

        {/* FORM */}
        <Reveal delay={0.1} className="lg:col-span-7">
          <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink">{t("con.form_title")}</h2>
          <p className="mt-3 text-sm text-mutedtx">{t("con.form_intro")}</p>
          <form onSubmit={submit} data-testid="contact-form-submission" className="mt-8 space-y-6" noValidate>
            {/* Honeypot antispam — invisibile agli umani */}
            <div className="honeypot-field" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => set({ website: e.target.value })}
                data-testid="contact-honeypot-input"
              />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="cf-name" className={labelCls}>{t("con.form.name")}</label>
                <input id="cf-name" type="text" value={form.name} placeholder={t("con.form.name_ph")} data-testid="contact-name-input" className={inputCls} onChange={(e) => set({ name: e.target.value })} />
              </div>
              <div>
                <label htmlFor="cf-email" className={labelCls}>{t("con.form.email")}</label>
                <input id="cf-email" type="email" value={form.email} placeholder="nome@esempio.it" data-testid="contact-email-input" className={inputCls} onChange={(e) => set({ email: e.target.value })} />
              </div>
            </div>
            <div>
              <label htmlFor="cf-subject" className={labelCls}>{t("con.form.subject")}</label>
              <input id="cf-subject" type="text" value={form.subject} placeholder={t("con.form.subject_ph")} data-testid="contact-subject-input" className={inputCls} onChange={(e) => set({ subject: e.target.value })} />
            </div>
            <div>
              <label htmlFor="cf-message" className={labelCls}>{t("con.form.message")}</label>
              <textarea id="cf-message" rows={6} value={form.message} placeholder={t("con.form.message_ph")} data-testid="contact-message-input" className={`${inputCls} resize-y`} onChange={(e) => set({ message: e.target.value })} />
            </div>
            <div className="flex items-start gap-3">
              <input
                id="cf-privacy"
                type="checkbox"
                checked={form.consent}
                data-testid="contact-privacy-checkbox"
                className="mt-1 h-4 w-4 accent-bordeaux"
                onChange={(e) => set({ consent: e.target.checked })}
              />
              <label htmlFor="cf-privacy" className="text-sm leading-relaxed text-bodytx/85">
                {t("con.form.privacy_pre")}{" "}
                <Link to="/privacy" data-testid="contact-privacy-policy-link" className="font-semibold text-bordeaux underline">
                  Privacy Policy
                </Link>{" "}
                *
              </label>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit-btn"
              className="inline-flex items-center gap-2 bg-ink px-8 py-3.5 text-[13px] font-semibold uppercase tracking-[0.12em] text-paper transition-colors duration-300 hover:bg-bordeaux disabled:cursor-wait disabled:opacity-60"
            >
              {sending ? t("con.form.sending") : t("con.form.send")}
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </Reveal>
      </div>
    </>
  );
};

export default Contatti;
